import { Button } from "./ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";
import { Badge } from "./ui/badge";
import {
	CheckCircle,
	Mail,
	Building2,
	Droplets,
	Wrench,
	Users,
	Shield,
} from "lucide-react";

interface ServiceDetailsProps {
	service: string;
	onBack: () => void;
}

// --- SEO/Lead-optimized, visually pleasing service data ---
const serviceData = {
	"civil-engineering": {
		title: "Civil Engineering Services",
		subtitle: "Comprehensive site development and infrastructure design",
		icon: <Wrench className="h-8 w-8" />,
		color: "blue",
		bg: "bg-blue-50",
		description:
			"Our civil engineering team specializes in site development, infrastructure design, and construction management for projects throughout Central Texas. From initial site analysis to final construction oversight, we provide comprehensive solutions that meet local requirements and exceed client expectations.",
		services: [
			"Site Planning & Design",
			"Grading & Drainage Design",
			"Road & Parking Design",
			"Stormwater Management",
			"Infrastructure Coordination",
			"Construction Administration",
			"As-Built Documentation",
			"Post-Construction Services",
		],
		process: [
			{
				title: "Site Analysis",
				description:
					"Comprehensive evaluation of existing conditions and constraints",
			},
			{
				title: "Design Development",
				description: "Creating detailed plans that optimize site potential",
			},
			{
				title: "Regulatory Approval",
				description: "Navigating permitting and approval processes",
			},
			{
				title: "Construction Support",
				description: "Ongoing oversight and quality assurance",
			},
		],
		benefits: [
			"Optimized site utilization and cost efficiency",
			"Streamlined permitting and approval processes",
			"Reduced construction delays and cost overruns",
			"Long-term maintenance and operational benefits",
		],
	},
	"water-wastewater": {
		title: "Water & Wastewater Services",
		subtitle: "Expert tap plans and utility infrastructure design",
		icon: <Droplets className="h-8 w-8" />,
		color: "navy",
		bg: "bg-blue-50",

		description:
			"Specialized water and wastewater infrastructure services including tap plan design, utility coordination, and system optimization. Our team has extensive experience working with municipal utilities and private service providers throughout Central Texas.",
		services: [
			"Tap Plan Design & Approval",
			"Water Line Sizing & Design",
			"Wastewater Connection Design",
			"Utility Coordination",
			"System Capacity Analysis",
			"Pressure & Flow Studies",
			"Infrastructure Mapping",
			"Regulatory Compliance",
		],
		process: [
			{
				title: "Utility Coordination",
				description:
					"Working directly with service providers to determine requirements",
			},
			{
				title: "System Design",
				description: "Creating efficient and code-compliant tap plan designs",
			},
			{
				title: "Permit Submission",
				description: "Managing approval process with utility companies",
			},
			{
				title: "Construction Support",
				description: "Inspection and approval coordination",
			},
		],
		benefits: [
			"Faster utility connection approvals",
			"Reduced tap fees through optimal design",
			"Seamless coordination with utility providers",
			"Future expansion planning and capacity",
		],
	},
	permitting: {
		title: "Permitting & Compliance",
		subtitle: "Navigate complex local regulations with confidence",
		icon: <Building2 className="h-8 w-8" />,
		color: "blue",
		bg: "bg-blue-50",

		description:
			"Expert navigation of local permitting requirements across Central Texas jurisdictions. Our deep knowledge of local regulations and established relationships with permitting authorities ensure smooth approval processes for your projects.",
		services: [
			"City of Austin Permitting",
			"Travis County Development",
			"Environmental Compliance",
			"Zoning & Variance Applications",
			"TCEQ Permitting",
			"Historic District Reviews",
			"Code Compliance",
			"Inspection Coordination",
		],
		process: [
			{
				title: "Regulatory Review",
				description:
					"Comprehensive analysis of applicable codes and requirements",
			},
			{
				title: "Application Preparation",
				description: "Complete and accurate permit application packages",
			},
			{
				title: "Agency Coordination",
				description: "Direct liaison with permitting authorities",
			},
			{
				title: "Approval Management",
				description: "Tracking and expediting approval processes",
			},
		],
		benefits: [
			"Reduced permitting timelines and delays",
			"Higher approval rates on first submission",
			"Cost savings through efficient processes",
			"Risk mitigation and compliance assurance",
		],
	},
	"project-management": {
		title: "Project Management",
		subtitle: "End-to-end coordination and oversight",
		icon: <Users className="h-8 w-8" />,
		color: "navy",
		bg: "bg-blue-50",

		description:
			"Comprehensive project management services that ensure your development projects are completed on time, within budget, and to the highest quality standards. Our experienced team coordinates all aspects of the project lifecycle.",
		services: [
			"Project Planning & Scheduling",
			"Budget Development & Control",
			"Contractor Selection & Management",
			"Quality Assurance & Control",
			"Risk Assessment & Mitigation",
			"Stakeholder Communication",
			"Progress Reporting",
			"Closeout & Documentation",
		],
		process: [
			{
				title: "Project Initiation",
				description: "Defining scope, timeline, and budget parameters",
			},
			{
				title: "Planning & Design",
				description: "Coordinating all design and engineering activities",
			},
			{
				title: "Construction Management",
				description: "Overseeing construction activities and quality control",
			},
			{
				title: "Project Closeout",
				description: "Final inspections, documentation, and handover",
			},
		],
		benefits: [
			"Single point of contact for entire project",
			"Reduced administrative burden on clients",
			"Proactive issue identification and resolution",
			"Clear project scheduling and budget oversight",
		],
	},
};

export default function ServiceDetails({
	service,
	onBack,
}: ServiceDetailsProps) {
	const data = serviceData[service as keyof typeof serviceData];

	if (!data) {
		return <div>Service not found</div>;
	}

	const colorClasses = {
		blue: {
			gradient: "from-blue/10 to-blue/5",
			border: "border-blue/20",
			text: "text-blue",
			bg: "bg-blue/10",
			card: "bg-white/90 shadow-xl hover:shadow-2xl rounded-2xl",
		},
		navy: {
			gradient: "from-navy/10 to-navy/5",
			border: "border-navy/20",
			text: "text-navy",
			bg: "bg-navy/10",
			card: "bg-white/90 shadow-xl hover:shadow-2xl rounded-2xl",
		},
	};

	const colorConfig = colorClasses[data.color as keyof typeof colorClasses];

	return (
		<div className="min-h-screen bg-background flex flex-col items-center justify-center">
			{/* Hero Section */}
			<section className="relative py-20 overflow-hidden flex justify-center items-center w-full bg-gradient-to-b from-slate-50/60 to-background">
				<div
					className={`absolute inset-0 bg-gradient-to-br ${colorConfig.gradient} opacity-70`}
					aria-hidden="true"
				></div>
				<div className="relative w-full max-w-7xl 2xl:max-w-[1800px] 3xl:max-w-[2100px] mx-auto px-2 md:px-0 flex flex-col items-center justify-center text-center space-y-8">
					<Badge variant="secondary" className="mb-4">
						<span className={`mr-2 text-navy`}>{data.icon}</span>
						Specialized Service
					</Badge>
					<h1 className="text-4xl md:text-6xl font-extrabold text-primary tracking-tight leading-tight">
						{data.title}
					</h1>
					<p className="text-xl text-muted-foreground max-w-2xl mx-auto">
						{data.description}
					</p>
					<div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
						<Button size="lg" asChild>
							<a href="/contact">Get Free Consultation</a>
						</Button>
					</div>
				</div>
			</section>

			{/* Services Overview */}
			<section className="py-20">
				<div className="w-full max-w-7xl 2xl:max-w-[1800px] 3xl:max-w-[2100px] mx-auto px-2 md:px-0">
					<div className="grid lg:grid-cols-2 gap-16 items-start">
						<div>
							<h2 className="text-3xl md:text-4xl mb-6 font-bold text-primary">
								Services Included
							</h2>
							<div className="grid grid-cols-1 gap-4">
								{data.services.map((service, index) => (
									<div
										key={index}
										className="flex items-center gap-3 p-4 bg-secondary/20 rounded-lg border border-blue/10"
									>
										<CheckCircle
											className={`h-5 w-5 ${colorConfig.text} flex-shrink-0`}
										/>
										<span>{service}</span>
									</div>
								))}
							</div>
						</div>

						<div className="space-y-8">
							<Card
								className={`${colorConfig.card} border-2 ${colorConfig.border}`}
							>
								<CardContent className="p-8">
									<h3 className="mb-6 text-xl font-bold text-primary">
										Why Choose Our {data.title}?
									</h3>
									<div className="space-y-4">
										{data.benefits.map((benefit, index) => (
											<div key={index} className="flex items-start gap-3">
												<Shield
													className={`h-5 w-5 mt-0.5 flex-shrink-0 ${colorConfig.text}`}
												/>
												<span className="text-base">{benefit}</span>
											</div>
										))}
									</div>
								</CardContent>
							</Card>

							<Card
								className={`${colorConfig.card} border ${colorConfig.border}`}
							>
								<CardHeader>
									<CardTitle className="text-lg font-bold">
										Ready to Get Started?
									</CardTitle>
									<CardDescription>
										Contact us for a free consultation and detailed project
										proposal.
									</CardDescription>
								</CardHeader>
								<CardContent className="space-y-4">
									<div className="flex items-center gap-3">
										<Mail className="h-5 w-5 text-primary" />
										<div>
											<div>miguel@govaengineering.com</div>
											<div className="text-sm text-muted-foreground">
												24-hour email response
											</div>
										</div>
									</div>
									<Button className="w-full font-semibold text-base" asChild>
										<a href="/contact">Request Consultation</a>
									</Button>
								</CardContent>
							</Card>
						</div>
					</div>
				</div>
			</section>

			{/* Process Section */}
			<section className="py-20 bg-secondary/20">
				<div className="container">
					<div className="text-center mb-16">
						<h2 className="text-3xl md:text-4xl mb-4">Our Process</h2>
						<p className="text-muted-foreground max-w-2xl mx-auto">
							We follow a proven methodology to ensure project success and
							client satisfaction.
						</p>
					</div>

					<div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
						{data.process.map((step, index) => (
							<Card key={index} className={`${colorConfig.border} border`}>
								<CardHeader>
									<div className="w-12 h-12 bg-primary text-primary-foreground rounded-lg flex items-center justify-center mb-4">
										<span className="text-lg">{index + 1}</span>
									</div>
									<CardTitle className="text-lg">{step.title}</CardTitle>
								</CardHeader>
								<CardContent>
									<p className="text-sm text-muted-foreground">
										{step.description}
									</p>
								</CardContent>
							</Card>
						))}
					</div>
				</div>
			</section>

			{/* Contact Section */}
			<section id="contact" className="py-20">
				<div className="container">
					<div className="max-w-2xl mx-auto text-center">
						<h2 className="text-3xl md:text-4xl mb-6">
							Start Your {data.title} Project
						</h2>
						<p className="text-muted-foreground mb-8">
							Contact our {data.title.toLowerCase()} specialists for a free
							consultation and project estimate.
						</p>
						<div className="flex flex-col sm:flex-row gap-4 justify-center">
							<Button variant="outline" size="lg" asChild>
								<a
									href="mailto:miguel@govaengineering.com"
									className="flex items-center gap-2"
								>
									<Mail className="h-4 w-4" />
									Email Us
								</a>
							</Button>
						</div>
					</div>
				</div>
			</section>
		</div>
	);
}
